import { useState } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { UserFormDialog, SetPasswordDialog } from "@/components/users/user-dialogs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiError } from "@/lib/api";
import { displayName, type User } from "@/lib/auth";
import { usersApi, usersQueryKey, usersQueryOptions } from "@/lib/users";

export const Route = createFileRoute("/_authenticated/users/")({
  beforeLoad: ({ context }) => {
    if (!context.user.is_admin) throw redirect({ to: "/" });
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(usersQueryOptions),
  component: UsersPage,
});

function formatDate(value: string | null): string {
  if (!value) return "never";
  return new Date(value).toLocaleString();
}

function UsersPage() {
  const { user: me } = Route.useRouteContext();
  const { data } = useSuspenseQuery(usersQueryOptions);
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [resetting, setResetting] = useState<User | null>(null);

  const toggleActive = useMutation({
    mutationFn: (user: User) => usersApi.update(user.id, { is_active: !user.is_active }),
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: usersQueryKey });
      toast.success(updated.is_active ? "User activated" : "User deactivated");
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : "Could not update user"),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
          <p className="text-muted-foreground text-sm">{data.count} accounts in this deployment.</p>
        </div>
        <Button onClick={() => setCreating(true)}>New user</Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last login</TableHead>
              <TableHead className="w-[1%]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.results.map((user) => (
              <TableRow key={user.id} className={user.is_active ? "" : "text-muted-foreground"}>
                <TableCell className="font-medium">
                  {displayName(user)}
                  {user.id === me.id && (
                    <span className="text-muted-foreground ml-2 text-xs">(you)</span>
                  )}
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Badge variant={user.is_admin ? "default" : "secondary"}>{user.role}</Badge>
                </TableCell>
                <TableCell>{user.is_active ? "Active" : "Inactive"}</TableCell>
                <TableCell>{formatDate(user.last_login)}</TableCell>
                <TableCell className="space-x-1 whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => setEditing(user)}>
                    Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setResetting(user)}>
                    Password
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={user.id === me.id || toggleActive.isPending}
                    onClick={() => toggleActive.mutate(user)}
                  >
                    {user.is_active ? "Deactivate" : "Activate"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <UserFormDialog open={creating} onOpenChange={setCreating} user={null} />
      <UserFormDialog
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        user={editing}
      />
      <SetPasswordDialog user={resetting} onOpenChange={(open) => !open && setResetting(null)} />
    </div>
  );
}
